import type { ReactNode } from 'react'
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type TonoEstado = 'ok' | 'aviso' | 'error' | 'neutro'

interface PropiedadesEstado {
  tono: TonoEstado
  titulo: string
  detalle?: ReactNode
  /** Se anuncia a lectores de pantalla cuando el estado cambia solo. */
  enVivo?: boolean
  children?: ReactNode
}

/**
 * QUÉ FORMA TIENE CADA TONO, ADEMÁS DE QUÉ COLOR.
 *
 * Hasta aquí el tono se distinguía SOLO por el color de un punto: verde, ocre,
 * rojo o gris. Quien no distingue esos colores —o quien mira la pantalla en el
 * modo de contraste forzado, donde el sistema sustituye la paleta entera— veía
 * cuatro puntos idénticos. El icono es la redundancia que faltaba: cada tono
 * tiene ahora una silueta distinta, y la silueta sobrevive a cualquier cambio
 * de color porque es geometría.
 *
 * Las cuatro formas se eligieron por contorno, no por bonitas: círculo con
 * palomita, triángulo, círculo con admiración y círculo con «i». Se reconocen
 * en miniatura y son las mismas que usa el resto del mundo para lo mismo.
 */
const ICONO_POR_TONO: Record<TonoEstado, LucideIcon> = {
  ok: CircleCheck,
  aviso: TriangleAlert,
  error: CircleAlert,
  neutro: Info,
}

/**
 * Bloque de estado con texto explícito. Nunca es un indicador mudo: siempre
 * dice qué pasó y, cuando aplica, qué se puede hacer.
 */
export function Estado({
  tono,
  titulo,
  detalle,
  enVivo = false,
  children,
}: PropiedadesEstado) {
  const clase = tono === 'neutro' ? 'estado' : `estado estado--${tono}`
  const Icono = ICONO_POR_TONO[tono]
  return (
    <div
      className={clase}
      role={tono === 'error' ? 'alert' : 'status'}
      aria-live={enVivo ? 'polite' : 'off'}
    >
      <ESTILOS />
      {/* Decorativo, y con todas sus letras: el título de al lado ya dice qué
          pasó, así que anunciar además «imagen» sería repetir. `focusable` va
          explícito porque Internet Explorer y algunos motores antiguos meten
          los SVG en el orden de tabulación aunque no tengan nada que activar. */}
      <Icono className="estado__icono" aria-hidden="true" focusable="false" />
      <div>
        <p className="estado__titulo">{titulo}</p>
        {detalle !== undefined && <p className="estado__detalle">{detalle}</p>}
        {children !== undefined && (
          <div className="estado__acciones">{children}</div>
        )}
      </div>
    </div>
  )
}

/**
 * Los estilos del icono viajan con el componente, como en `Ayuda`, `Tour` y
 * `Esqueleto`: `precedence` monta una sola copia en el `<head>` por más bloques
 * de estado que haya en la pantalla. Ni un color literal: los cuatro tonos son
 * los mismos tokens que ya pintaban el punto, y el icono los toma por
 * `currentColor`, así que el tema oscuro lo sigue sin una regla más.
 */
function ESTILOS() {
  return (
    <style href="cubiculos-estado" precedence="componente">{`
      /* El icono ocupa el lugar que tenía el punto dentro del flex de
         .estado, que reparte con align-items: flex-start. El desplazamiento
         hacia abajo alinea su centro óptico con la primera línea del título,
         no con el borde superior de la caja. */
      .estado__icono {
        width: 1.25rem;
        height: 1.25rem;
        flex: none;
        margin-top: 0.125rem;
        color: var(--estado-neutro);
      }

      .estado--ok .estado__icono { color: var(--estado-ok); }
      .estado--aviso .estado__icono { color: var(--estado-aviso); }
      .estado--error .estado__icono { color: var(--estado-error); }
    `}</style>
  )
}
